import React from 'react';
import { Box, ActionIcon, Stack, Flex, Anchor, Button } from '@mantine/core';
import {
  IconArrowBarLeft,
  IconArrowBarRight,
  IconHome,
  IconWorld,
  IconReport,
  IconPolaroid,
  IconSettings,
  IconReportAnalytics,
  IconMoneybag,
  IconUser,
  IconHeadset,
  IconPower,
} from '@tabler/icons-react';
import { useAuth } from '../../../Context/useAuth'; // ✅ import useAuth (adjust path if needed)
import { useNavigate } from 'react-router-dom'; // ✅ because logout may need navigate
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  hovering: boolean;
  setHovering: (hovering: boolean) => void;
}

const navItems = [
  { link: "/dashboard", label: "sidebar.home", icon: IconHome },
  { link: "/domains", label: "sidebar.domains", icon: IconWorld },
  { link: "/privacy-audits", label: "sidebar.privacyAudits", icon: IconReport },
  { link: "/consentBanners", label: "sidebar.consentBanner", icon: IconPolaroid },
  { link: "/integration", label: "sidebar.integration", icon: IconSettings },
  { link: "/analytics", label: "sidebar.analytics", icon: IconReportAnalytics },
  { link: "/billingSubscriptions", label: "sidebar.billingSubscriptions", icon: IconMoneybag },
  { link: "/profile", label: "sidebar.profile", icon: IconUser },
  { link: "", label: "sidebar.helpSupport", icon: IconHeadset },
  { link: "logout", label: "sidebar.logout", icon: IconPower },
];


function Sidebar({ expanded, setExpanded, hovering, setHovering }: SidebarProps): JSX.Element {
  const isOpen = expanded || hovering;
  const { logout } = useAuth(); // ✅ getting logout function
  const { t } = useTranslation();

  return (
    <Box
      style={{
        width: isOpen ? '250px' : '64px',
        backgroundColor: '#0078b4',
        height: '100%',
        position: isOpen ? 'absolute' : 'relative',
        left: 0,
        top: 0,
        zIndex: isOpen ? 200 : 1,
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        padding: '8px',
        borderRight: '1px solid rgba(255,255,255,0.1)',
      }}
      onMouseEnter={() => !expanded && setHovering(true)}
      onMouseLeave={() => !expanded && setHovering(false)}
    >
      <ActionIcon
        variant="subtle"
        color="gray.2"
        style={{
          position: 'absolute',
          right: 20,
          top: 10,
          transition: 'transform 0.3s ease',
          transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)',
        }}
        onClick={() => {
          setExpanded(!expanded);
          setHovering(false);
        }}
      >
        {isOpen ? <IconArrowBarLeft size={20} /> : <IconArrowBarRight size={20} />}
      </ActionIcon>

      <Stack gap="xs" mt={40}>
        {navItems.map((item) => (
          t(item.label) === t("sidebar.logout") ? (
            <Button
              key={item.label}
              variant="subtle"
              color="gray.2"
              style={{
                background: "none",
                border: "none",
                padding: "8px",
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "#fff",
                cursor: "pointer",
              }}
              onClick={() => logout("/")}
            >
              <Flex align="center" gap="sm">
              <item.icon size={20} style={{ color: '#fff' }} />
              {isOpen && t(item.label)}
              </Flex>
            </Button>
          ) : (
            <Anchor
              key={item.link || item.label}
              href={item.link}
              underline="never"
              style={{
                display: 'block',
                borderRadius: '6px',
                padding: '8px',
                color: '#fff',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.color = '#fff';
                const icon = e.currentTarget.querySelector('svg');
                if (icon) icon.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#fff';
                const icon = e.currentTarget.querySelector('svg');
                if (icon) icon.style.color = '#fff';
              }}
            >
              <Flex align="center" gap="sm">
                <item.icon size={20} style={{ color: '#fff', transition: 'color 0.2s ease' }} />
                {isOpen && t(item.label)}
              </Flex>
            </Anchor>
          )
        ))}
      </Stack>
    </Box>
  );
}

export { Sidebar };
